import {
  SupportingActorType,
  SupportingActorUserProfile
} from '@dadohr/shared/domainTypes/experience';
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select
} from '@material-ui/core';
import { useMemo } from 'react';
import { CustomInputLabel } from '../../../../../../../components/CustomInputLabel';
import { HalfWidthField } from '../../ActorDialogParts';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FlexContainerVertical } from '../../../../../../../layout/Flex';
import { filterUserRelationshipCustomFields } from '../../../../../../../services/orgs';
import type {
  ActorTypeFieldsProps,
  ActorTypeForm,
  ProfileActorState
} from '..';

const RELATIONSHIP_FIELDS = [{ label: 'managerId', key: 'managerId' }];

const ProfileActorFields = ({
  state,
  onChange,
  disabled,
  showValidation,
  customFields
}: ActorTypeFieldsProps<ProfileActorState>) => {
  const { t } = useUnsafeTranslation();
  const relationshipFields = useMemo(
    () => [
      ...RELATIONSHIP_FIELDS,
      ...filterUserRelationshipCustomFields(customFields).map((f) => ({
        label: f.key,
        key: `customFields.${f.key}`
      }))
    ],
    [customFields]
  );
  return (
    <>
      <HalfWidthField>
        <CustomInputLabel fullWidth label={t('ID in user data')}>
          <FlexContainerVertical fullWidth spacing={0}>
            <Select
              variant="outlined"
              fullWidth
              value={state.field ?? ''}
              error={showValidation && !state.field}
              onChange={(ev) =>
                onChange({ ...state, field: ev.target.value as string })
              }
              disabled={disabled}
            >
              {relationshipFields.map((f) => (
                <MenuItem key={f.key} value={f.key}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>
            {showValidation && !state.field && (
              <FormHelperText error>
                {t("Please select a field on the participant's user profile")}
              </FormHelperText>
            )}
          </FlexContainerVertical>
        </CustomInputLabel>
      </HalfWidthField>
      <FormControlLabel
        disabled={disabled}
        control={
          <Checkbox
            color="primary"
            checked={state.ignoreWhenMissing}
            onChange={(ev) =>
              onChange({ ...state, ignoreWhenMissing: ev.target.checked })
            }
          />
        }
        label={t('Optional')}
      />
    </>
  );
};

/** "Referenced in each participant's profile": a field of their user data. */
export const profileActorType: ActorTypeForm<ProfileActorState> = {
  option: SupportingActorType.USER,
  label: (t) => t(`Referenced in each participant's profile`),
  matches: (actor) => actor.type === SupportingActorType.USER,
  fromActor: (actor, defaultColor) => {
    const a = actor as SupportingActorUserProfile;
    return {
      name: a.name || '',
      field: a.field || null,
      type: SupportingActorType.USER,
      color: a.color || defaultColor,
      ignoreWhenMissing: !!a.ignoreWhenMissing
    };
  },
  create: (base) => ({
    name: base.name,
    type: SupportingActorType.USER,
    field: null,
    color: base.color,
    ignoreWhenMissing: base.ignoreWhenMissing
  }),
  isComplete: (state) => !!state.field,
  toActor: (state, key) => ({
    type: SupportingActorType.USER,
    name: state.name,
    key,
    source: 'PROFILE',
    field: state.field!,
    color: state.color,
    ignoreWhenMissing: state.ignoreWhenMissing
  }),
  Fields: ProfileActorFields
};
